// frontend/src/components/MessageBubble.jsx
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

const MessageBubble = ({ message, onFeedback }) => {
  const { role, text, citations = [], id, conversation_examples, conversation_difficulty, crisis_data, response_tone } = message;
  const isUser = role === 'user';
  const [showExamples, setShowExamples] = useState(false);
  const [showCrisisResources, setShowCrisisResources] = useState(false);
  
  // Check if there's a translation (indicated by separator)
  const parts = text.split(/---+/);
  const hasTranslation = parts.length > 1;
  
  // For student UI - extract only the answer portion of the translation
  const extractTranslatedAnswer = (translationText) => {
    // Look for the pattern "Answer in English: [answer]"
    const match = translationText.match(/Answer in English:\s*(.*)/s);
    if (match && match[1]) {
      return match[1].trim();
    }
    
    // If the pattern isn't found, look for content after the pipe symbol
    const pipeParts = translationText.split(/\|/);
    if (pipeParts.length > 1) {
      return pipeParts[1].replace(/Answer in English:/i, '').trim();
    }
    
    // If no specific patterns are found, return the whole translation part
    return translationText.trim();
  };
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-3/4 rounded-lg p-3 ${
        isUser 
          ? 'bg-indigo-500 text-white rounded-tr-none' 
          : 'bg-white border border-gray-200 shadow-sm rounded-tl-none'
      }`}>
        {/* User message */}
        {isUser ? (
          <p className="text-base text-white">{text}</p>
        ) : (
          <div className="text-base prose prose-base max-w-none">
            {/* For messages with translation */}
            {hasTranslation ? (
              <>
                <div>
                  <ReactMarkdown>{parts[0].trim()}</ReactMarkdown>
                </div>
                {parts.length > 1 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-2">English Translation:</div>
                    <ReactMarkdown>{extractTranslatedAnswer(parts[1])}</ReactMarkdown>
                  </div>
                )}
              </>
            ) : (
              <ReactMarkdown>{text}</ReactMarkdown>
            )}
          </div>
        )}
        
        {/* Tone Indicator - show for assistant responses */}
        {!isUser && response_tone && response_tone !== 'casual' && (
          <div className="mt-1 flex justify-end">
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
              {response_tone === 'formal' && '🎓 Professional tone'}
              {response_tone === 'slang' && '🤙 Teen-friendly tone'}
            </span>
          </div>
        )}
        
        {/* Crisis Resources - show if crisis detected */}
        {!isUser && crisis_data && crisis_data.detected && crisis_data.resources && crisis_data.resources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-red-200">
            <button
              onClick={() => setShowCrisisResources(!showCrisisResources)}
              className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              <span>🆘</span>
              <span>{showCrisisResources ? 'Hide' : 'Show'} crisis resources</span>
              <span className="text-xs">
                {showCrisisResources ? '▲' : '▼'}
              </span>
            </button>
            
            {showCrisisResources && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <span className="text-red-500 text-lg">🆘</span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-800 mb-2">Crisis Resources Available</h4>
                    <p className="text-sm text-red-700 mb-3">
                      You don't have to face this alone. Here are resources that can help:
                    </p>
                    <div className="space-y-2">
                      {crisis_data.resources.map((resource, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-red-200">
                          <div className="font-medium text-red-800">{resource.name}</div>
                          <div className="text-sm text-red-700">{resource.description}</div>
                          <div className="text-sm space-y-1 mt-1">
                            {resource.phone && (
                              <div>
                                <span className="font-medium">Call:</span> 
                                <a href={`tel:${resource.phone}`} className="ml-1 text-red-600 hover:underline">
                                  {resource.phone}
                                </a>
                              </div>
                            )}
                            {resource.text && (
                              <div>
                                <span className="font-medium">Text:</span> 
                                <span className="ml-1 text-red-600">{resource.text}</span>
                              </div>
                            )}
                            {resource.website && (
                              <div>
                                <span className="font-medium">Website:</span> 
                                <a href={resource.website} target="_blank" rel="noopener noreferrer" className="ml-1 text-red-600 hover:underline">
                                  {resource.website}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {crisis_data.level === 'critical' && (
                      <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm">
                        <strong className="text-red-800">If this is an emergency, call 911 immediately.</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Conversation Examples - only show if available and NOT in crisis situation */}
        {!isUser && conversation_examples && (!crisis_data || !crisis_data.detected) && (conversation_examples.starters?.length > 0 || conversation_examples.examples?.length > 0 || conversation_examples.follow_ups?.length > 0) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <button
              onClick={() => setShowExamples(!showExamples)}
              className="flex items-center space-x-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <span>💬</span>
              <span>{showExamples ? 'Hide' : 'Show'} conversation examples</span>
              <span className="text-xs">
                {showExamples ? '▲' : '▼'}
              </span>
            </button>
            
            {showExamples && (
              <div className="mt-3 space-y-3">
                {conversation_examples.starters?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Getting Started:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {conversation_examples.starters.map((starter, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-indigo-500 mr-2">•</span>
                          <span>"{starter}"</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {conversation_examples.examples?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">During the Talk:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {conversation_examples.examples.map((example, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-indigo-500 mr-2">•</span>
                          <span>"{example}"</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {conversation_examples.follow_ups?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Keep it Going:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {conversation_examples.follow_ups.map((followUp, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-indigo-500 mr-2">•</span>
                          <span>"{followUp}"</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {conversation_difficulty && conversation_difficulty !== 'casual' && (
                  <div className="text-xs text-gray-500 italic">
                    Conversation level: {conversation_difficulty}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Feedback buttons */}
        {!isUser && (
          <div className="mt-2 flex justify-end space-x-2">
            <button 
              onClick={() => onFeedback(id, true)}
              className="text-xs text-gray-500 hover:text-green-500"
            >
              Helpful
            </button>
            <button 
              onClick={() => onFeedback(id, false)}
              className="text-xs text-gray-500 hover:text-red-500"
            >
              Not Helpful
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;